/**
 * 确认卡价格那一格的措辞 —— console 两端(PC 面板 / H5 ConfirmGate)**共用这一份**。
 *
 * ★为什么要有它:此前 PC 与 H5 **各自拼**「约 {est_credits} 积分」,而 `est_credits` 恒等于
 * 报价**上界**。ai 侧把报价改成区间后,军师那句话(bubble)里写「约 700~1900 积分」,
 * 紧挨着的确认卡上却写「约 1900 积分」—— **两处数字打架**,而用户会以卡为准(那是他要点的),
 * 两个数不一致本身就让人不信。
 *
 * ★字段与措辞规则都取自 ai 侧**真源码**(不照消息里的字符串手抄):
 * - 字段构造唯一口:`app/modules/advisor/action_tools.py::_quote_range()`
 *   → `{"est_credits": hi, "est_credits_low": low}`,**`est_credits` 恒等于上界**;
 * - 措辞规则:`app/agent/runtime/loop.py::_price_text()`
 *   → 下沿缺省或 `low >= high` 时**退回单值**,不写「700~700」这种废话。
 *
 * ⚠️ **跨仓契约锁暂缺,理由写在这里**:ai 侧那两处**尚未进 main**,锁若指向对方 worktree 路径,
 * 在 CI 上必然找不到。现阶段两边各写各的常量 + 注释互指对方文件;**两边都进 main 之后**再补
 * 真正的跨仓锁(读对面真源码断言字段名一致)。本仓栽过同族:ai 发 `blueprintId`、消费方只认
 * `taskId`,每次必抛却长得不像故障。
 */
export interface QuoteLike {
  /**
   * 报价上界。★0926 起 ai 在价表取不到价时**显式给 null**(以前有的口直接省略键),
   * summary 里写「价格暂时取不到,实扣按系统价目结算」。null / 缺省 = 未知,**不是 0**。
   */
  est_credits?: number | null;
  est_credits_low?: number | null;
}

/** 价表取不到价时卡上那一格的话术(PC / H5 同一句)。 */
export const PRICE_PENDING_TEXT = "价格待系统核算";
/** 真·免费(est_credits === 0 且无正下沿)时的话术,与 ai 侧 _price_text 零值一致。 */
export const PRICE_FREE_TEXT = "免费(不扣积分)";

export type QuotePrice =
  | { kind: "pending" }
  | { kind: "free" }
  | { kind: "single"; high: number }
  | { kind: "range"; low: number; high: number };

/**
 * 报价分类 —— 两端唯一判据。
 * ★「不知道多少钱」(null/undefined/非有限数)和「不要钱」(=== 0)绝不能同形:
 *   前者显 {@link PRICE_PENDING_TEXT},只有严格 `=== 0` 才算免费。
 */
export function classifyQuote(quote: QuoteLike): QuotePrice {
  const high = quote.est_credits;
  if (typeof high !== "number" || !Number.isFinite(high)) return { kind: "pending" };
  const low = quote.est_credits_low;
  const hasRange = typeof low === "number" && Number.isFinite(low) && low < high;
  if (high === 0 && !hasRange) return { kind: "free" };
  if (hasRange) return { kind: "range", low: low as number, high };
  return { kind: "single", high };
}

/** 价格措辞;与 ai 侧 `_price_text()` 同规则(含"下沿≥上界退回单值")。 */
export function priceText(quote: QuoteLike): string {
  const p = classifyQuote(quote);
  switch (p.kind) {
    case "pending":
      return PRICE_PENDING_TEXT;
    case "free":
      return PRICE_FREE_TEXT;
    case "range":
      return `约 ${p.low}~${p.high} 积分`;
    case "single":
      return `约 ${p.high} 积分`;
  }
}

/**
 * 把报价 summary 劈成「规格」与「本次不会出现」两段。
 *
 * ★只认**一个确切标记**,不做通用解析。ai 侧这句只有一个构造口
 *   (`app/modules/advisor/missing_inputs.py:123`):
 *     `"这几样你没给,成品里不会出现(要写就现在说一句):" + "、".join(missing)`
 *   所以在这个字面上劈一刀是安全的。
 * ★**不要**按 `·` 或 `/` 通用切分 —— ai 自己的 `_split_list` 就是这么把
 *   「0.10元/分钟」切碎的(0825 那批的根因之一)。报价只许抄不许算,
 *   显示层的重排也一样:切错一次就是把用户没答应的规格摆到他眼前。
 * ★劈不到标记时 `missing` 为空、`spec` 是整段原文 —— **一个字都不丢**。
 *   降级路径永远是「照原样显示」,不是「显示不出来」。
 */
const MISSING_MARK = "这几样你没给,成品里不会出现(要写就现在说一句):";
/**
 * 套图已定画幅时卡面别再说「画幅:按内容智能推荐」(1006 演示机复测:同一张卡上
 * 「全渠道套图,同内容出:9:16、16:9 · … · 画幅:按内容智能推荐」自相矛盾)。
 * 来源是 ai 报价摘要(action_tools 按单张 ratio 字段写画幅格,套图的 ratios 不进那一格),
 * console 显示层就地改成实际画幅;ai 侧修好后本函数自然空转。
 */
export function fixSuiteRatioPhrase(spec: string): string {
  const m = /同内容出[:：]\s*([^·]+?)\s*(?:·|$)/.exec(spec);
  if (!m) return spec;
  const ratios = m[1].trim();
  return spec.replace(/画幅[:：]\s*按内容智能推荐/, `画幅:${ratios}`);
}

export function splitQuoteSummary(summary: string): { spec: string; missing: string | null } {
  const i = summary.indexOf(MISSING_MARK);
  if (i < 0) return { spec: fixSuiteRatioPhrase(summary), missing: null };
  return {
    spec: fixSuiteRatioPhrase(summary.slice(0, i).replace(/[\s·・,,]+$/, "")),
    missing: summary.slice(i + MISSING_MARK.length).trim() || null,
  };
}
